import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { OfferStep, Step } from "@app/shared/models/step/step.model";
import { CalculateComponent } from "@app/calculate/calculate/calculate.component";
import { CalculateGuard } from "@app/calculate/calculate.guard";
import { SessionExpiredComponent } from "@app/shared/components/session/session-expired.component";
import { CalculateDictionaryService } from "@app/calculate/calculate/calculate-dictionary.service";
import { BreadcrumbService } from "@app/shared/components/breadcrumb/breadcrumb.service";
import { ExitPopupService } from "@app/shared/services/exit-popup/exit-popup.service";
import { GtmService } from "@app/shared/services/gtm/gtm.service";
import { CallbackService } from "@app/shared/components/callback/callback.service";
import { RangeUtil } from "@app/shared/services/range-util/range-util.service";
import { DateUtilService } from "@app/shared/services/date-util/date-util.service";
import { BodyStylerService } from "@app/shared/services/body-styler/body-styler.service";
import { ComparatorService } from "@app/shared/services/comparator/comparator.service";
import { GeneratorService } from "@app/shared/services/generator/generator.service";
import { PersonalDataStoreService } from "@app/shared/services/personal-data/personal-data-store.service";
import { UrlParamsStoreService } from "@app/shared/interceptors/partner/url-params-store.service";
import { RequestStoreService } from "@app/shared/services/request-store/request-store.service";
import { CalculationStoreService } from "@app/calculate/calculate/calculation-store.service";
import { CalculationService } from "@app/calculate/calculate/calculation.service";
import { SessionExpirationService } from "@app/shared/components/session/session-expiration.service";
import { CustomApmService } from "@app/shared/services/apm/custom-apm.service";
import { ScrollToFirstInvalidService } from "@app/shared/directives/scroll-to-invalid/scroll-to-invalid.service";
import { CalendarService } from "@app/shared/services/calendar/calendar.service";
import { CoverService } from "@app/shared/services/cover/cover.service";
import { CepikService } from "@app/shared/services/cepik/cepik.service";
import { CepikFormDataService } from "@app/calculate/calculate/cepik-form-data.service";
import { ExceptionMessageReplaceService } from "@app/shared/services/ui/exception-message-replace.service";
import { ConsentService } from "@app/shared/services/consent/consent.service";
import { OneTrustService } from "@app/shared/services/one-trust/one-trust.service";
import { SalesManagoService } from "@app/shared/services/sales-manago/sales-manago.service";
import { PolicyService } from "@app/offer/policy/policy-service";
import { PaymentService } from "@app/sell/payment/payment.service";
import { AppInitializerService } from "@app/shared/services/app/app-initializer.service";
import { OmniService } from "@app/shared/services/omni/omni.service";
import { VideoService } from "@app/shared/components/video/video.service";
import { VehicleService } from "@app/shared/services/vehicle/vehicle.service";
import { DatePickerService } from "@app/shared/components/datepicker/datepicker.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS
} from "@angular/material/core";
import { CustomDateAdapter } from "@app/shared/components/datepicker/dayjs-date-adapter";
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from "@angular/material/tooltip";

const appRoutes: Routes = [
  {
    path: "",
    component: CalculateComponent,
    canActivateChild: [CalculateGuard],
    providers: [CalculateDictionaryService],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./landing-page/landing-page.component").then(
            (c) => c.LandingPageComponent
          )
      },
      {
        path: Step.SESSION_EXPIRED,
        component: SessionExpiredComponent
      },
      {
        path: Step.SHORT_PATH,
        loadComponent: () =>
          import("./calculate/short-path/short-path.component").then(
            (c) => c.ShortPathComponent
          )
      },
      {
        path: Step.VEHICLE,
        loadComponent: () =>
          import("./calculate/vehicle/vehicle.component").then(
            (c) => c.VehicleComponent
          )
      },
      {
        path: Step.VEHICLE_ADDITIONAL_INFO,
        loadComponent: () =>
          import(
            "./calculate/vehicle-additional-info/vehicle-additional-info.component"
          ).then((c) => c.VehicleAdditionalInfoComponent)
      },
      {
        path: Step.VEHICLE_OWNER,
        loadComponent: () =>
          import("./calculate/vehicle-owner/vehicle-owner.component").then(
            (c) => c.VehicleOwnerComponent
          )
      },
      {
        path: Step.VEHICLE_OWNER_DETAILS,
        loadComponent: () =>
          import(
            "./calculate/vehicle-owner-details/vehicle-owner-details.component"
          ).then((c) => c.VehicleOwnerDetailsComponent),
        data: {
          step: Step.VEHICLE_OWNER_DETAILS
        }
      },
      {
        path: Step.VEHICLE_FIRST_CO_OWNER_DETAILS,
        loadComponent: () =>
          import(
            "./calculate/vehicle-owner-details/vehicle-owner-details.component"
          ).then((c) => c.VehicleOwnerDetailsComponent),
        data: {
          step: Step.VEHICLE_FIRST_CO_OWNER_DETAILS
        }
      },
      {
        path: Step.VEHICLE_SECOND_CO_OWNER_DETAILS,
        loadComponent: () =>
          import(
            "./calculate/vehicle-owner-details/vehicle-owner-details.component"
          ).then((c) => c.VehicleOwnerDetailsComponent),
        data: {
          step: Step.VEHICLE_SECOND_CO_OWNER_DETAILS
        }
      },
      {
        path: Step.INSURANCE_START_DATE,
        loadComponent: () =>
          import(
            "./calculate/insurance-start-date/insurance-start-date.component"
          ).then((c) => c.InsuranceStartDateComponent)
      },
      {
        path: OfferStep.OMNI_OFFER,
        loadComponent: () =>
          import("./offer/omni-offer/omni-offer.component").then(
            (c) => c.OmniOfferComponent
          )
      },
      {
        path: OfferStep.OMNI_ADDITIONAL_OPTION,
        loadComponent: () =>
          import(
            "./offer/omni-additional-option/omni-additional-option.component"
          ).then((c) => c.OmniAdditionalOptionComponent)
      },
      {
        path: OfferStep.INSURANCE_VARIANT,
        loadComponent: () =>
          import("./offer/insurance-variant/insurance-variant.component").then(
            (c) => c.InsuranceVariantComponent
          )
      },
      {
        path: OfferStep.INSURANCE_ADDITIONAL_OPTION,
        loadComponent: () =>
          import(
            "./offer/insurance-additional-option/insurance-additional-option.component"
          ).then((c) => c.InsuranceAdditionalOptionComponent)
      },
      {
        path: OfferStep.YOUR_OFFER,
        loadComponent: () =>
          import("./offer/your-offer/your-offer.component").then(
            (c) => c.YourOfferComponent
          )
      },
      {
        path: OfferStep.VEHICLE_REGISTRATION_DATA,
        loadComponent: () =>
          import(
            "./offer/vehicle-registration-data/vehicle-registration-data.component"
          ).then((c) => c.VehicleRegistrationDataComponent)
      },
      {
        path: OfferStep.VEHICLE_OWNER_ADDITIONAL_DETAILS,
        loadComponent: () =>
          import(
            "./offer/vehicle-owner-additional-details/vehicle-owner-additional-details.component"
          ).then((c) => c.VehicleOwnerAdditionalDetailsComponent),
        data: {
          step: OfferStep.VEHICLE_OWNER_ADDITIONAL_DETAILS
        }
      },
      {
        path: OfferStep.VEHICLE_FIRST_CO_OWNER_ADDITIONAL_DETAILS,
        loadComponent: () =>
          import(
            "./offer/vehicle-owner-additional-details/vehicle-owner-additional-details.component"
          ).then((c) => c.VehicleOwnerAdditionalDetailsComponent),
        data: {
          step: OfferStep.VEHICLE_FIRST_CO_OWNER_ADDITIONAL_DETAILS
        }
      },
      {
        path: OfferStep.VEHICLE_SECOND_CO_OWNER_ADDITIONAL_DETAILS,
        loadComponent: () =>
          import(
            "./offer/vehicle-owner-additional-details/vehicle-owner-additional-details.component"
          ).then((c) => c.VehicleOwnerAdditionalDetailsComponent),
        data: {
          step: OfferStep.VEHICLE_SECOND_CO_OWNER_ADDITIONAL_DETAILS
        }
      },
      {
        path: OfferStep.CONTACT_AND_CONSENT,
        loadComponent: () =>
          import(
            "./offer/contact-and-consent/contact-and-consent.component"
          ).then((c) => c.ContactAndConsentComponent)
      },
      {
        path: OfferStep.INCONSISTENT_TOTAL_PRICE,
        loadComponent: () =>
          import(
            "./offer/inconsistent-total-price/inconsistent-total-price.component"
          ).then((c) => c.InconsistentTotalPriceComponent)
      },
      {
        path: OfferStep.SHORT_PATH_DETAILS,
        loadComponent: () =>
          import(
            "./offer/short-path-details/short-path-details.component"
          ).then((c) => c.ShortPathDetailsComponent)
      },
      {
        path: OfferStep.THANK_YOU,
        loadComponent: () =>
          import("./sell/thank-you/thank-you.component").then(
            (c) => c.ThankYouComponent
          )
      },
      {
        path: OfferStep.THANK_YOU_FOR_PAYMENT,
        loadComponent: () =>
          import("./sell/thank-you-payment/thank-you-payment.component").then(
            (c) => c.ThankYouPaymentComponent
          )
      },
      {
        path: OfferStep.PAYMENT_RETURN_VIEW,
        loadComponent: () =>
          import(
            "./sell/payment-return-view/payment-return-view.component"
          ).then((c) => c.PaymentReturnViewComponent)
      },
      // każdy nieznany przekieruj na landing-page
      {
        path: "**",
        redirectTo: ""
      }
    ]
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      enableTracing: false, // <-- debugging purposes only
      useHash: false,
      scrollPositionRestoration: "top"
    })
  ],
  exports: [RouterModule],
  providers: [
    BreadcrumbService,
    ExitPopupService,
    GtmService,
    CallbackService,
    RangeUtil,
    DateUtilService,
    BodyStylerService,
    ComparatorService,
    GeneratorService,
    PersonalDataStoreService,
    UrlParamsStoreService,
    RequestStoreService,
    CalculationStoreService,
    CalculateGuard,
    CalculationService,
    SessionExpirationService,
    CustomApmService,
    ScrollToFirstInvalidService,
    CalendarService,
    CoverService,
    CepikService,
    CepikFormDataService,
    ExceptionMessageReplaceService,
    ConsentService,
    OneTrustService,
    SalesManagoService,
    PolicyService,
    PaymentService,
    AppInitializerService,
    OmniService,
    VideoService,
    VehicleService,
    DatePickerService,
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MAT_DATE_LOCALE, useValue: "pl" },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "DD-MM-YYYY"
        },
        display: {
          dateInput: "DD-MM-YYYY",
          monthYearLabel: "MMM YYYY",
          dateA11yLabel: "LL",
          monthYearA11yLabel: "MMMM YYYY"
        }
      }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 0,
        hideDelay: 0,
        touchendHideDelay: 1000,
        position: "before"
      }
    }
  ]
})
export class AppRoutingModule {}
